import React, { useEffect, useRef, useState } from "react";

function prefersReducedMotion() {
  return (
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

function isCoarsePointer() {
  return (
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(pointer: coarse)").matches
  );
}

function lerp(a, b, n) {
  return (1 - n) * a + n * b;
}

export default function MomentumScroll({
  children,
  enabled = true,
  lerpFactor = 0.07,
  id = "fk-main",
}) {
  const [active, setActive] = useState(false);
  const mainRef = useRef(null);
  const rafRef = useRef(null);
  const roRef = useRef(null);

  // Reveal engine (single source of truth, avoids snapping with momentum)
  const revealTargetsRef = useRef([]);
  const revealArmedRef = useRef(false);
  const revealRafRef = useRef(0);
  const revealScheduledRef = useRef(false);
  const revealFrameRef = useRef(0);
  const revealSeenFrameRef = useRef(new WeakMap());

  const refreshRevealTargets = (root) => {
    const frame = revealFrameRef.current;
    const seenMap = revealSeenFrameRef.current;
    revealTargetsRef.current = Array.from(root.querySelectorAll("[data-fk-reveal]"))
      .filter((el) => !el.classList.contains("is-visible"))
      .map((el) => {
        if (!seenMap.has(el)) seenMap.set(el, frame);
        return el;
      });
  };

  const revealTick = () => {
    if (!revealArmedRef.current) return;
    const targets = revealTargetsRef.current;
    if (!targets.length) return;

    const vh = window.innerHeight || 0;
    const threshold = 0.86; // reveal before fully entering view
    const frame = revealFrameRef.current;
    const seenMap = revealSeenFrameRef.current;

    const remaining = [];
    for (const el of targets) {
      const seenAt = seenMap.get(el) ?? frame;
      // Ensure every element gets at least ~2 frames to paint hidden before revealing.
      if (frame - seenAt < 2) {
        remaining.push(el);
        continue;
      }
      const r = el.getBoundingClientRect();
      const inView = r.top < vh * threshold && r.bottom > 0;
      if (inView) el.classList.add("is-visible");
      else remaining.push(el);
    }
    revealTargetsRef.current = remaining;
  };

  const scheduleReveal = () => {
    if (revealScheduledRef.current) return;
    revealScheduledRef.current = true;
    revealRafRef.current = window.requestAnimationFrame(() => {
      revealScheduledRef.current = false;
      revealFrameRef.current += 1;
      if (mainRef.current) refreshRevealTargets(mainRef.current);
      revealTick();
    });
  };

  useEffect(() => {
    const main = mainRef.current;
    if (!enabled || !main) return undefined;

    // Arm reveals after two paints so transitions always run (no snapping).
    revealArmedRef.current = false;
    refreshRevealTargets(main);
    const arm1 = window.requestAnimationFrame(() => {
      const arm2 = window.requestAnimationFrame(() => {
        revealArmedRef.current = true;
        scheduleReveal();
      });
      revealRafRef.current = arm2;
    });

    // Always support reveals (even when momentum is disabled).
    window.addEventListener("scroll", scheduleReveal, { passive: true });
    window.addEventListener("resize", scheduleReveal, { passive: true });

    // If momentum is allowed, enable it; otherwise keep normal flow.
    const allowMomentum = !prefersReducedMotion() && !isCoarsePointer();
    if (!allowMomentum) {
      return () => {
        window.removeEventListener("scroll", scheduleReveal);
        window.removeEventListener("resize", scheduleReveal);
        if (arm1) window.cancelAnimationFrame(arm1);
        if (revealRafRef.current) window.cancelAnimationFrame(revealRafRef.current);
        revealArmedRef.current = false;
        revealScheduledRef.current = false;
      };
    }

    setActive(true);

    const body = document.body;
    const html = document.documentElement;

    const getHeaderH = () => {
      const raw = window
        .getComputedStyle(document.documentElement)
        .getPropertyValue("--fk-header-h")
        .trim();
      const fromVar = Number.parseFloat(raw);
      if (Number.isFinite(fromVar) && fromVar > 0) return Math.ceil(fromVar);
      const headerEl = document.querySelector("[data-fk-header]");
      return headerEl ? Math.ceil(headerEl.getBoundingClientRect().height) : 0;
    };

    let headerH = getHeaderH();

    let sx = 0;
    let sy = 0;
    let dx = 0;
    let dy = 0;

    const updateBodyHeight = () => {
      // Use scrollHeight to capture full content height.
      headerH = getHeaderH();
      const h = Math.ceil(main.scrollHeight + headerH);
      body.style.height = `${h}px`;
    };

    updateBodyHeight();

    // Ensure we don't fight other pages' layouts.
    main.style.position = "fixed";
    main.style.top = "var(--fk-header-h)";
    main.style.left = "0px";
    main.style.right = "0px";
    main.style.willChange = "transform";
    // Keep the momentum layer under the fixed navbar.
    main.style.zIndex = "0";

    // Prevent accidental horizontal overflow.
    html.style.overflowX = "hidden";

    const onScroll = () => {
      sx = window.pageXOffset || 0;
      sy = window.pageYOffset || 0;
    };

    window.addEventListener("scroll", onScroll, { passive: true });

    roRef.current = new ResizeObserver(() => updateBodyHeight());
    roRef.current.observe(main);

    const render = () => {
      revealFrameRef.current += 1;
      dx = lerp(dx, sx, lerpFactor);
      dy = lerp(dy, sy, lerpFactor);

      dx = Math.floor(dx * 100) / 100;
      dy = Math.floor(dy * 100) / 100;

      main.style.transform = `translate3d(${-dx}px, ${-dy}px, 0px)`;
      // Reveal elements based on their transformed viewport position.
      refreshRevealTargets(main);
      revealTick();
      rafRef.current = window.requestAnimationFrame(render);
    };

    rafRef.current = window.requestAnimationFrame(render);

    return () => {
      setActive(false);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("scroll", scheduleReveal);
      window.removeEventListener("resize", scheduleReveal);
      if (rafRef.current) window.cancelAnimationFrame(rafRef.current);
      if (roRef.current) roRef.current.disconnect();
      if (arm1) window.cancelAnimationFrame(arm1);
      if (revealRafRef.current) window.cancelAnimationFrame(revealRafRef.current);

      // Reset styles we touched.
      body.style.height = "";
      html.style.overflowX = "";
      main.style.position = "";
      main.style.top = "";
      main.style.left = "";
      main.style.right = "";
      main.style.transform = "";
      main.style.willChange = "";
      main.style.zIndex = "";

      revealArmedRef.current = false;
      revealScheduledRef.current = false;
    };
  }, [enabled, lerpFactor]);

  return (
    <div id={id} ref={mainRef} data-fk-momentum={active ? "true" : "false"}>
      {children}
    </div>
  );
}

